/**
 * "Burn a fuel" explorer for the Combustion & Corrosion Lab (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"). The learner sets the fuel, its mass and the oxygen available.
 * With enough oxygen the model calculates complete combustion; with too little it says
 * qualitatively that combustion is incomplete and does not invent a CO / soot split.
 *
 * Reviewed data reused: the balanced complete-combustion equations in PROCESS_EQUATIONS
 * (combustionCorrosion.ts) and classroom atomic masses through calculateMolarMass
 * (periodicTableData.ts). reviewStatus: 'pending-teaching-review'.
 */
import { calculateMolarMass } from './chemistryCalculations';
import { PROCESS_EQUATIONS, type FlameColour, type ProcessEquationCase } from './combustionCorrosion';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError, formatModelNumber } from './modelInputContract';
import { formatFormula } from './reactionPredictionData';

export interface FuelBurnSetup {
  fuelMassG: number;
  oxygenMassG: number;
}

export type FuelBurnInput = keyof FuelBurnSetup;

export const FUEL_BURN_FIELDS: ModelField<FuelBurnInput>[] = [
  { key: 'fuelMassG', label: 'Mass of fuel', unit: 'g', min: 0.1, max: 100, step: 0.1 },
  { key: 'oxygenMassG', label: 'Oxygen available', unit: 'g', min: 0, max: 500, step: 0.1 },
];

export const DEFAULT_FUEL_BURN_SETUP: FuelBurnSetup = { fuelMassG: 1.6, oxygenMassG: 10 };

/** Reviewed complete-combustion equations whose fuel is a hydrocarbon or alcohol (C, H and maybe O). */
export const BURN_FUELS: ProcessEquationCase[] = PROCESS_EQUATIONS.filter(
  (entry) => entry.process === 'combustion' && /C/.test(entry.reactants[0].formula) && /H/.test(entry.reactants[0].formula),
);

export const DEFAULT_FUEL_ID = 'methane';

export function burnFuelById(id: string): ProcessEquationCase {
  const fuel = BURN_FUELS.find((entry) => entry.id === id);
  if (!fuel) throw new Error(`No reviewed fuel: ${id}`);
  return fuel;
}

export type BurnKind = 'complete' | 'incomplete' | 'none';

export interface FuelBurnResult {
  fuel: ProcessEquationCase;
  kind: BurnKind;
  /** Flame expected for the oxygen supply; null when nothing burns. */
  flame: FlameColour | null;
  fuelMol: number;
  oxygenNeededMol: number;
  oxygenNeededG: number;
  oxygenMol: number;
  /** Oxygen available as a share of the oxygen needed (may exceed 1). */
  oxygenShare: number;
  /** Product masses; null when combustion is not complete (the model does not invent a split). */
  carbonDioxideG: number | null;
  waterG: number | null;
  oxygenLeftG: number | null;
  equation: string;
  steps: WorkedStep[];
  limitNote: string | null;
}

function molarMass(formula: string): number {
  const result = calculateMolarMass(formula);
  if (!result.value) throw new Error(`No molar mass for ${formula}: ${result.error ?? 'unknown'}`);
  return result.value.molarMass;
}

/** Three significant figures, Australian formatting, without exponent notation. */
function sig3(value: number): string {
  if (value === 0) return '0';
  return value.toLocaleString('en-AU', { maximumSignificantDigits: 3 });
}

export function fuelEquation(fuel: ProcessEquationCase): string {
  const terms = (species: ProcessEquationCase['reactants'], offset: number) =>
    species.map((entry, index) => {
      const coefficient = fuel.balanced[offset + index];
      return `${coefficient === 1 ? '' : coefficient}${formatFormula(entry.formula)}(${entry.state})`;
    });
  return `${terms(fuel.reactants, 0).join(' + ')} → ${terms(fuel.products, fuel.reactants.length).join(' + ')}`;
}

/** Amounts, oxygen check and (when complete) product masses. Throws on invalid input (fail closed). */
export function calculateFuelBurn(fuel: ProcessEquationCase, setup: FuelBurnSetup): FuelBurnResult {
  const error = firstModelError(FUEL_BURN_FIELDS, setup);
  if (error) throw new Error(error);
  const [fuelCoefficient, oxygenCoefficient, carbonDioxideCoefficient, waterCoefficient] = fuel.balanced;
  const fuelFormula = fuel.reactants[0].formula;
  const fuelLabel = formatFormula(fuelFormula);
  const fuelM = molarMass(fuelFormula);
  const oxygenM = molarMass('O2');
  const carbonDioxideM = molarMass('CO2');
  const waterM = molarMass('H2O');

  const fuelMol = setup.fuelMassG / fuelM;
  const oxygenNeededMol = (fuelMol * oxygenCoefficient) / fuelCoefficient;
  const oxygenNeededG = oxygenNeededMol * oxygenM;
  const oxygenMol = setup.oxygenMassG / oxygenM;
  const oxygenShare = oxygenMol / oxygenNeededMol;
  const kind: BurnKind = setup.oxygenMassG === 0 ? 'none' : oxygenShare >= 1 ? 'complete' : 'incomplete';
  const complete = kind === 'complete';

  const carbonDioxideMol = (fuelMol * carbonDioxideCoefficient) / fuelCoefficient;
  const waterMol = (fuelMol * waterCoefficient) / fuelCoefficient;
  const carbonDioxideG = carbonDioxideMol * carbonDioxideM;
  const waterG = waterMol * waterM;
  const oxygenLeftG = (oxygenMol - oxygenNeededMol) * oxygenM;
  const ratio = (coefficient: number) => (fuelCoefficient === 1 ? `${coefficient}` : `${coefficient}/${fuelCoefficient}`);
  const productStatus: WorkedStep['status'] = complete ? 'done' : 'not-reached';
  const notCalculated = kind === 'none' ? 'nothing burns' : 'not calculated: oxygen runs out';

  const steps: WorkedStep[] = [
    {
      id: 'fuel',
      title: `Amount of ${fuel.reactants[0].name}`,
      formula: 'n = m ÷ M',
      working: `n(${fuelLabel}) = ${sig3(setup.fuelMassG)} g ÷ ${formatModelNumber(fuelM, 2)} g/mol = ${sig3(fuelMol)} mol`,
      status: 'done',
    },
    {
      id: 'oxygen-needed',
      title: 'Oxygen needed for complete combustion',
      formula: `n(O₂) = n(${fuelLabel}) × ${ratio(oxygenCoefficient)}, m = n × M`,
      working: `${sig3(fuelMol)} mol × ${ratio(oxygenCoefficient)} = ${sig3(oxygenNeededMol)} mol × ${formatModelNumber(oxygenM, 2)} g/mol = ${sig3(oxygenNeededG)} g`,
      status: 'done',
    },
    {
      id: 'oxygen-available',
      title: 'Oxygen available',
      formula: 'n = m ÷ M',
      working: `n(O₂) = ${sig3(setup.oxygenMassG)} g ÷ ${formatModelNumber(oxygenM, 2)} g/mol = ${sig3(oxygenMol)} mol`,
      status: 'done',
    },
    {
      id: 'compare',
      title: 'Is there enough oxygen?',
      formula: 'compare available with needed',
      working:
        kind === 'none'
          ? 'No oxygen, so the fuel cannot burn: the fire triangle needs oxygen.'
          : `${sig3(oxygenMol)} mol ${complete ? '≥' : '<'} ${sig3(oxygenNeededMol)} mol → ${
              complete ? 'enough oxygen: complete combustion' : `only ${formatModelNumber(oxygenShare * 100, 0)} % of the oxygen needed: incomplete combustion`
            }`,
      status: 'done',
    },
    {
      id: 'carbon-dioxide',
      title: 'Carbon dioxide formed',
      formula: `n(CO₂) = n(${fuelLabel}) × ${ratio(carbonDioxideCoefficient)}, m = n × M`,
      working: `${sig3(carbonDioxideMol)} mol × ${formatModelNumber(carbonDioxideM, 2)} g/mol = ${sig3(carbonDioxideG)} g`,
      status: productStatus,
      note: complete ? undefined : notCalculated,
    },
    {
      id: 'water',
      title: 'Water formed',
      formula: `n(H₂O) = n(${fuelLabel}) × ${ratio(waterCoefficient)}, m = n × M`,
      working: `${sig3(waterMol)} mol × ${formatModelNumber(waterM, 2)} g/mol = ${sig3(waterG)} g`,
      status: productStatus,
      note: complete ? undefined : notCalculated,
    },
    {
      id: 'mass-check',
      title: 'Mass is conserved',
      formula: 'fuel + O₂ used = CO₂ + H₂O',
      working: `${sig3(setup.fuelMassG)} g + ${sig3(oxygenNeededG)} g = ${sig3(carbonDioxideG)} g + ${sig3(waterG)} g = ${sig3(carbonDioxideG + waterG)} g`,
      status: productStatus,
      note: complete ? undefined : notCalculated,
    },
  ];

  const limitNote =
    kind === 'none'
      ? 'With no oxygen nothing burns. Add oxygen to see combustion.'
      : kind === 'incomplete'
        ? `Only ${formatModelNumber(oxygenShare * 100, 0)} % of the oxygen needed. With limited oxygen some carbon forms carbon monoxide (CO, a toxic gas) or soot (C) instead of CO₂, and some fuel may not burn. The amounts depend on the conditions, so the model does not calculate them.`
        : null;

  return {
    fuel,
    kind,
    flame: kind === 'none' ? null : complete ? 'blue' : 'yellow',
    fuelMol,
    oxygenNeededMol,
    oxygenNeededG,
    oxygenMol,
    oxygenShare,
    carbonDioxideG: complete ? carbonDioxideG : null,
    waterG: complete ? waterG : null,
    oxygenLeftG: complete ? oxygenLeftG : null,
    equation: fuelEquation(fuel),
    steps,
    limitNote,
  };
}

// ---------------------------------------------------------------------------------------------
// Teaching goal (CT-INTERACT C7): one question and three tasks checked on the result
// ---------------------------------------------------------------------------------------------

export const FUEL_BURN_QUESTION = 'How much oxygen does a fuel need to burn completely, and where does the mass go?';

/** "Just enough" accepts complete combustion with at most 5 % more oxygen than needed. */
export const JUST_ENOUGH_MAX_SHARE = 1.05;
/** Target band for the carbon dioxide task, in grams. */
export const CO2_TARGET_G = { min: 9.5, max: 10.5 } as const;

/** Teaching tasks. Each check reads only the model result, never the UI. */
export const FUEL_BURN_CHALLENGES: ExploreChallenge<FuelBurnResult>[] = [
  {
    id: 'incomplete',
    prompt: 'Change only the oxygen so that the flame turns yellow.',
    hint: 'Give the fuel a little less oxygen than the "Oxygen needed" bar.',
    isMet: (result) => result.kind === 'incomplete',
    why: 'Less oxygen does not just make a smaller flame. Below the oxygen the equation needs (6.38 g for 1.6 g of methane), some carbon forms toxic carbon monoxide or soot instead of CO₂, which gives a yellow, sooty flame.',
  },
  {
    id: 'just-enough',
    prompt: 'Give exactly enough oxygen for complete combustion (no more than 5 % left over).',
    hint: 'Read the oxygen needed in the worked steps and type a value just above it.',
    isMet: (result) => result.kind === 'complete' && result.oxygenShare <= JUST_ENOUGH_MAX_SHARE,
    why: 'CH₄ + 2O₂ needs 2 mol of O₂ for each mol of methane: 0.0997 mol × 2 × 32 g/mol = 6.38 g of oxygen for 1.6 g of fuel, about four times the mass of the fuel itself.',
  },
  {
    id: 'co2-10g',
    prompt: `Burn the fuel completely and make ${CO2_TARGET_G.min}–${CO2_TARGET_G.max} g of carbon dioxide.`,
    hint: 'Increase the mass of fuel, then make sure there is still enough oxygen.',
    isMet: (result) =>
      result.kind === 'complete' &&
      result.carbonDioxideG !== null &&
      result.carbonDioxideG >= CO2_TARGET_G.min &&
      result.carbonDioxideG <= CO2_TARGET_G.max,
    why: 'Mass is not destroyed when a fuel burns. 3.6 g of methane makes 9.9 g of CO₂ (plus water), more than the fuel, because the products also hold the oxygen atoms taken from the air: fuel + oxygen used = CO₂ + water.',
  },
];
